import type { DimensionScore } from '../utils/api-client/api-schemas.ts'
import { coerceInternetFacing, type InternetFacing } from '../utils/threat-model/exposure.ts'
import { meanScore } from '../utils/threat-model/scorecard-score.ts'

/**
 * A repository's risk score, derived from its threat-model scorecard.
 *
 * **Risk is derived from posture, never independent of it.** Posture — the scorecard mean — answers
 * "how well defended is this repository". Risk answers "which should I look at first", which is the
 * posture *gap* weighted by how reachable the repository is. Two repositories with the same posture and
 * the same exposure must score the same, which is what stops the two numbers contradicting each other.
 *
 * Replaces the fabricated `RepositoryRisk` payload the ISS-8362 prototype assumed. That payload named
 * ten dimensions the scorecard already scores, so nothing here is invented except the exposure weights.
 */

/**
 * Keyed on the shared exposure vocabulary rather than a local enum.
 *
 * `utils/threat-model/exposure.ts` exists because a second definition of this vocabulary already grew
 * once and disagreed with the first about unrecognised values. A risk score and the estate's exposure
 * figures must classify a repository the same way, so the coercion is imported, not rewritten.
 *
 * Multiplicative, and deliberately gentle: exposure scales the posture gap rather than adding to it, so
 * a repository with a clean scorecard scores 0 however reachable it is. Adding a fixed bonus would have
 * made a perfectly-defended public service read as risky, which is not what the scorecard says.
 *
 * `unknown` is weighted as `partial` rather than as `no`: an undetermined exposure should not earn a
 * repository the discount that being demonstrably unreachable earns.
 */
const EXPOSURE_WEIGHTS: Record<InternetFacing, number> = {
  yes: 1,
  partial: 0.8,
  unknown: 0.8,
  no: 0.55,
}

/**
 * What the score still leaves out, in one place.
 *
 * Both the "About risk" reference and the per-repository drawer have to say this, and they said it in
 * their own words with nothing tying them together — so wiring confirmed findings in would have meant
 * finding and updating two strings. One constant, interpolated by both.
 */
export const CONFIRMED_FINDINGS_CAVEAT = 'Confirmed findings are not counted yet'

/**
 * The scorable dimensions, weakest first — ties broken on name so the order does not swap between
 * refetches. Non-finite scores are dropped: there is no ranking a dimension that did not score.
 *
 * **One definition on purpose.** The row's "Weakest: X at N%" and the drawer's weakest-dimensions list
 * have to agree about which dimension is weakest for the same repository. Written twice, a change to
 * the tie-break would silently move one and not the other.
 */
export const weakestDimensionsFirst = (dimensions: DimensionScore[], limit?: number): DimensionScore[] =>
  dimensions
    .filter(dimension => Number.isFinite(dimension.score))
    .sort((left, right) => left.score - right.score || left.displayName.localeCompare(right.displayName))
    .slice(0, limit)

/**
 * The single dimension holding the score back, so the row can say why the number is what it is.
 * `undefined` when nothing scored — there is no weakest of nothing.
 */
export const weakestDimension = (dimensions: DimensionScore[]): DimensionScore | undefined =>
  weakestDimensionsFirst(dimensions).at(0)

export type RiskFromScorecardInput = {
  dimensions: DimensionScore[]
  /** `exposure.internet_facing` from the threat model's content, verbatim. */
  internetFacing: string | undefined
}

/**
 * 0–100, higher is riskier, matching the scale the donut and the bands already render.
 *
 * `undefined` when no dimension carries a usable score — a scorecard that could not be scored is not
 * the same as a repository at zero risk, and returning 0 would rank it as the safest in the estate.
 */
export const riskFromScorecard = ({ dimensions, internetFacing }: RiskFromScorecardInput): number | undefined => {
  const posture = meanScore(dimensions.map(dimension => dimension.score))
  if (posture === undefined) {
    return undefined
  }

  // The gap, not the posture: posture is "how good", and risk counts what is missing.
  const gap = 100 * (1 - posture)
  const weight = EXPOSURE_WEIGHTS[coerceInternetFacing(internetFacing ?? '')]
  return Math.round(gap * weight)
}
